import constants from '@Common/Core/Constant';
import { CustomFont, FontFamily } from '@InstanceFactory';

import { properties } from '../Core/Properties';
import { enumerable } from '@Common/Core/CoreDecorator';

export default class FontHelper {
    public static FontFamilies = [
        { id: 'Arial', name: 'Arial' },
        { id: 'Arial Black', name: 'Arial Black' },
        { id: 'Arial Unicode MS', name: 'Arial Unicode MS' },
        { id: 'Calibri', name: 'Calibri' },
        { id: 'Cambria', name: 'Cambria' },
        { id: 'Cambria Math', name: 'Cambria Math' },
        { id: 'Comic Sans MS', name: 'Comic Sans MS' },
        { id: 'Candara', name: 'Candara' },
        { id: 'Consolas', name: 'Consolas' },
        { id: 'Constantia', name: 'Constantia' },
        { id: 'Corbel', name: 'Corbel' },
        { id: 'Courier New', name: 'Courier New' },
        { id: 'Georgia', name: 'Georgia' },
        { id: 'Noto Sans', name: 'Noto Sans' },
        { id: 'Open Sans', name: 'Open Sans' },
        { id: 'Segoe UI', name: 'Segoe UI' },
        { id: 'Symbol', name: 'Symbol' },
        { id: 'Tahoma', name: 'Tahoma' },
        { id: 'Times New Roman', name: 'Times New Roman' },
        { id: 'Trebuchet MS', name: 'Trebuchet MS' },
        { id: 'Verdana', name: 'Verdana' }
    ];

    public static applyFontFamily(fontFamily: FontFamily, doc: HTMLDocument = document): Promise<void> {
        const loader = new Promise<void>(resolve => {
            const oldStyle = doc.head.querySelector('#ff' + fontFamily.id);
            if (oldStyle) {
                oldStyle.remove();
            }
            const style = doc.createElement('style');
            style.setAttribute('id', 'ff' + fontFamily.id);
            if (!properties.browser.msie) {
                style.addEventListener('load', () => resolve, false);
            } else {
                Promise.wait(1).then(resolve);
            }
            style.appendChild(document.createTextNode(this.generateFontFace(fontFamily)));
            doc.head.appendChild(style);
        });

        return Promise.race([Promise.wait(200), loader]);
    }

    public static addFontFamily(fontFamily: FontFamily): void {
        if (!this.fontFamilies.contains(fontFamily)) {
            const index = this.fontFamilies.findIndex(ff => ff.name === fontFamily.name);
            this.fontFamilies[index >= 0 ? index : this.fontFamilies.length] = fontFamily;
        }
        this.FontFamilies.push({ id: fontFamily.name, name: fontFamily.name });
        this.FontFamilies = _(this.FontFamilies)
            .uniqBy(ff => ff.id)
            .orderBy(ff => ff.id)
            .value();
    }

    public static async loadFontFamilies(doc: Document = document, fontFamilies: FontFamily[] = this.fontFamilies): Promise<void> {
        const customFontsLoaders = _.map(fontFamilies, async ff => {
            this.addFontFamily(ff);
            await this.applyFontFamily(ff, doc);
            return await this.preloadFontFamily(ff);
        });

        const loaders = await Promise.all(customFontsLoaders);
        await Promise.wait(100);

        for (const loader of loaders) {
            loader.remove();
        }
    }

    @enumerable
    private static get fontFamilies(): FontFamily[] {
        const { data, insightMeta } = altosoft.model;
        const { fontFamilies } = insightMeta || data || { fontFamilies: [] };
        return fontFamilies;
    }

    private static preloadFontFamily(ff: FontFamily): Loader {
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('xlink', 'http://www.w3.org/1999/xlink');

        for (const font of ff.fonts) {
            const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            text.setAttribute('visibility', 'hidden');
            text.setAttribute('style', this.fontFaceCommonOption(ff, font));
            text.textContent = 'text';

            svg.appendChild(text);
        }

        document.body.appendChild(svg);
        return {
            remove() {
                document.body.removeChild(svg);
            }
        };
    }

    private static generateFontFace(fontFamily: FontFamily): string {
        return fontFamily.fonts
            .map(font => {
                const query = new URLSearchParams({ fontFamily: fontFamily.name, fontId: font.id });
                return `@font-face {
                        ${this.fontFaceCommonOption(fontFamily, font)}
                        ${font.unicodeRange ? `unicode-range: ${font.unicodeRange};` : ''}
                        src: url('${altosoft.baseAppPath}api/FontDataHandler/?${query}')
                    }`;
            })
            .join(constants.newline);
    }

    public static fontFaceCommonOption({ name }: { name: string }, font: CustomFont): string {
        return `font-family: '${name}';` + (font.isBold ? ' font-weight: bold;' : '') + (font.isItalic ? ' font-style: italic;' : '');
    }
}

type Loader = { remove(): void };
